import { timelineSplitDql, type SearchWindow, type TimesliceForm } from '@bloo-team/api-client'

/**
 * Entity search — the top-bar "everything about this value" search
 * (docs/entity-search-requirements.md; tenant probes in
 * docs/inventory/ddm-field-map.md).
 *
 * Everything in this module is PURE: classify a typed value, decide which
 * normalised fields it can live in, compile the cross-stream count query and
 * the per-stream row queries. The hook (entity-search.hook.ts) owns the jobs.
 *
 * Two facts verified live on 2026-08-26 shape every query here:
 *
 * 1. The pipe dialect accepts `stream=*` WITH a where clause, and
 *    `| groupby Stream` returns per-stream hit counts in ONE job (~30 s at
 *    1d, >120 s at 7d). A field that exists in only some streams does not
 *    error — the other streams simply never match.
 * 2. A field the data model DECLARES but no stream has ever carried
 *    (`ComputerName`) fails the whole job with `UNRESOLVED_COLUMN`. So the
 *    predicate must be pruned to fields the engine actually knows, and the
 *    hook retries once with the primary fields when it is not.
 */

export type EntityKind =
  | 'ip'
  | 'cidr'
  | 'email'
  | 'url'
  | 'domain'
  | 'hash'
  | 'file'
  | 'action'
  | 'user'
  | 'host'
  | 'any'

export const ENTITY_KINDS: readonly EntityKind[] = [
  'ip', 'cidr', 'email', 'url', 'domain', 'hash', 'file', 'action', 'user', 'host', 'any',
]

export function isEntityKind(v: unknown): v is EntityKind {
  return typeof v === 'string' && (ENTITY_KINDS as readonly string[]).includes(v)
}

/** Short human label per kind, for chips and result headers. */
export const KIND_LABEL: Record<EntityKind, string> = {
  ip: 'IP address',
  cidr: 'CIDR range',
  email: 'email',
  url: 'URL',
  domain: 'domain',
  hash: 'hash',
  file: 'file / process',
  action: 'action',
  user: 'user',
  host: 'host',
  any: 'anything',
}

/* ------------------------------------------------------------ field roles */

/**
 * Which normalised fields a kind can live in, OR-ed together in one
 * predicate. Order matters: the FIRST entries are the primary roles the
 * UNRESOLVED_COLUMN retry falls back to (see PRIMARY_COUNT).
 *
 * Names are the bare pipe-dialect spellings from the DDM map. SIGNALS keeps
 * its own vocabulary (Suspect… / Target…), listed too so signals join the
 * same search rather than needing a second one.
 */
export const KIND_FIELDS: Record<EntityKind, readonly string[]> = {
  ip: ['SrcIP', 'DstIP', 'DevSrcIP', 'ActorIPAddress', 'DeviceIP'],
  cidr: ['SrcIP', 'DstIP', 'DevSrcIP'],
  email: ['User', 'TargetUser', 'SuspectUser'],
  url: ['URL', 'SuspectUrl'],
  domain: ['Domain', 'DomainName', 'Query', 'QueryName', 'DstHost', 'Host'],
  hash: ['Hash', 'SuspectHash'],
  file: ['File', 'FileName', 'FilePath', 'Object', 'Process', 'SuspectObject', 'SuspectProcess'],
  action: ['Action', 'SuspectAction'],
  user: ['User', 'TargetUser', 'UserID', 'SuspectUser'],
  host: ['System', 'SrcHost', 'DstHost', 'SuspectHost', 'TargetHost'],
  any: ['User', 'TargetUser', 'System', 'SrcHost', 'DstHost', 'Domain', 'URL', 'File', 'Process', 'SuspectUser', 'SuspectHost', 'TargetHost'],
}

/** How many leading KIND_FIELDS entries count as "primary" per kind. */
const PRIMARY_COUNT: Record<EntityKind, number> = {
  ip: 2, cidr: 2, email: 2, url: 1, domain: 2, hash: 1, file: 3, action: 1, user: 2, host: 3, any: 5,
}

export function primaryFields(kind: EntityKind): string[] {
  return KIND_FIELDS[kind].slice(0, PRIMARY_COUNT[kind])
}

/**
 * The fields to actually query: the kind's roles, pruned to a vocabulary
 * when one is known. The vocabulary is the union of every stream's field
 * list (vfields / ddm), compared case-insensitively because spellings are
 * per-stream. With no vocabulary — or a vocabulary that knows none of the
 * roles — the primary fields go through unpruned; the engine's own
 * UNRESOLVED_COLUMN answer then drives the retry.
 */
export function resolveFields(kind: EntityKind, vocabulary?: Iterable<string> | null): string[] {
  const roles = KIND_FIELDS[kind]
  if (!vocabulary) return [...roles]
  const known = new Set<string>()
  for (const f of vocabulary) known.add((f.startsWith('$') ? f.slice(1) : f).toLowerCase())
  if (known.size === 0) return [...roles]
  const kept = roles.filter((r) => known.has(r.toLowerCase()))
  return kept.length ? kept : primaryFields(kind)
}

/* -------------------------------------------------------------- classify */

export interface Classification {
  kind: EntityKind
  value: string
  /** True when a bare token could be a user OR a host — chips resolve it. */
  ambiguous: boolean
  /** Other readings worth offering as chips (always includes `any` for tokens). */
  alternatives: EntityKind[]
}

const IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/
const IPV6 = /^(?=.*:)[0-9a-f:]{2,39}$/i
const CIDR = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\/(\d|[12]\d|3[0-2])$/
const EMAIL = /^[^\s@'"]+@[^\s@'"]+\.[a-z]{2,}$/i
const URL_RE = /^[a-z][a-z0-9+.-]*:\/\/\S+$/i
const HASH = /^(?:[0-9a-f]{32}|[0-9a-f]{40}|[0-9a-f]{64})$/i
const DOMAIN = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,24}$/i
const ACTION = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/
const FILE = /[\\/]|\.(?:exe|dll|ps1|bat|cmd|sh|py|js|vbs|docm?|xlsm?|pdf|zip|rar|7z|jar|msi|scr|lnk|iso|img)$/i
/** A bare token: what a username or a short hostname looks like. */
const TOKEN = /^[a-z0-9][a-z0-9._$@\\-]{0,127}$/i

/**
 * Classify one typed value. First match wins; a bare token is AMBIGUOUS
 * (user or host) and returns `user` with `host`/`any` as alternatives so the
 * UI can offer chips instead of guessing. `knownActions` lets an exact match
 * against the scope's Action enum win over the shape test.
 */
export function classifyValue(raw: string, knownActions?: ReadonlySet<string>): Classification | null {
  const v = raw.trim().replace(/^["']|["']$/g, '')
  if (!v || v.length > 2048 || /\s/.test(v)) return null
  const one = (kind: EntityKind, alternatives: EntityKind[] = []): Classification => ({
    kind, value: v, ambiguous: false, alternatives,
  })
  if (CIDR.test(v)) return one('cidr')
  if (IPV4.test(v) || IPV6.test(v)) return one('ip')
  if (HASH.test(v)) return one('hash')
  if (EMAIL.test(v)) return one('email', ['user'])
  if (URL_RE.test(v)) return one('url')
  if (knownActions?.has(v) || ACTION.test(v)) return one('action', ['any'])
  // Files before domains: `procdump.exe` is domain-shaped too.
  if (FILE.test(v)) return one('file', ['any'])
  if (DOMAIN.test(v)) return one('domain', ['host', 'url'])
  if (TOKEN.test(v)) {
    return { kind: 'user', value: v, ambiguous: true, alternatives: ['host', 'any'] }
  }
  return null
}

/* ---------------------------------------------------------------- queries */

/** Single-quoted always — an all-digit user id must not go bare. */
export function quoteValue(v: string): string {
  return `'${v.replace(/'/g, "''")}'`
}

/**
 * The OR predicate for one value across its role fields. A CIDR becomes a
 * dotted prefix LIKE on /8, /16, /24 (the only forms a string prefix can
 * express honestly); other masks widen to the containing octet boundary and
 * the caller shows that it did.
 */
export function entityPredicate(kind: EntityKind, value: string, fields: readonly string[], rawLogEvent = false): string {
  let parts: string[]
  if (kind === 'cidr') {
    const prefix = cidrPrefix(value)
    parts = fields.map((f) => `${f} LIKE ${quoteValue(`${prefix}%`)}`)
  } else {
    parts = fields.map((f) => `${f} = ${quoteValue(value)}`)
  }
  if (rawLogEvent) parts.push(`LogEvent LIKE ${quoteValue(`%${kind === 'cidr' ? cidrPrefix(value) : value}%`)}`)
  return parts.join(' OR ')
}

/** `10.1.2.0/24` -> `10.1.2.`; masks between boundaries round DOWN to the wider one. */
export function cidrPrefix(cidr: string): string {
  const [ip, maskStr] = cidr.split('/')
  const mask = Number(maskStr)
  const octets = (ip ?? '').split('.')
  const keep = mask >= 24 ? 3 : mask >= 16 ? 2 : mask >= 8 ? 1 : 0
  return keep === 0 ? '' : `${octets.slice(0, keep).join('.')}.`
}

/** The time bound a search runs under: a duration stage or an absolute window. */
export type EntityTime = { kind: 'relative'; duration: string } | { kind: 'absolute'; start: string; end: string }

export const WINDOW_PRESETS = ['1h', '24h', '7d', '30d'] as const
export const DEFAULT_WINDOW = '24h'

function head(pred: string, stream: string): string {
  return `stream=${stream} where ${pred}`
}

function durationStage(time: EntityTime): string[] {
  return time.kind === 'relative' ? [`duration ${time.duration}`] : []
}

export function windowOf(time: EntityTime): SearchWindow | undefined {
  return time.kind === 'absolute' ? { start: time.start, end: time.end } : undefined
}

/** The per-stream hit count, ONE job for the whole scope. */
export function countQuery(pred: string, time: EntityTime): string {
  return [head(pred, '*'), ...durationStage(time), 'groupby Stream', 'select Stream, count(Stream)'].join(' | ')
}

export const ROW_LIMIT = 500

/** The rows for one stream that the count job said has hits. */
export function rowsQuery(stream: string, pred: string, time: EntityTime, limit = ROW_LIMIT): string {
  return [head(pred, stream), ...durationStage(time), `limit ${limit}`].join(' | ')
}

/**
 * One stacked histogram for every stream at once: the split-timeline
 * grammar (`groupby <bucket>, Stream`) over the `stream=*` head. Null when
 * the split grammar is known-refused on this engine.
 */
export function timelineQuery(pred: string, time: EntityTime, slice: string, form: TimesliceForm): string | null {
  const base = [head(pred, '*'), ...durationStage(time), 'limit 1'].join(' | ')
  return timelineSplitDql(base, slice, form, 'CNAMTime', 'Stream')
}

/** "Open in Search" for a tab: the same rows query, editable as text. */
export function openInSearchQuery(stream: string, pred: string, time: EntityTime): string {
  return rowsQuery(stream, pred, time, 100)
}

/* ------------------------------------------------------------- count rows */

export interface StreamCount {
  stream: string
  count: number
}

/**
 * Read the count job's rows by NAME, never position: the value column is
 * `Stream` as this result spells it, the count column is the engine's
 * synthetic `count_col1` (or whatever non-Stream numeric column it chose).
 */
export function readStreamCounts(fields: readonly string[], rows: ReadonlyArray<Record<string, unknown>>): StreamCount[] {
  const streamCol = fields.find((f) => f.toLowerCase() === 'stream') ?? fields[0]
  const countCol =
    fields.find((f) => /_col\d+$/i.test(f)) ??
    fields.find((f) => f !== streamCol && rows.some((r) => typeof r[f] === 'number'))
  if (!streamCol || !countCol) return []
  const out: StreamCount[] = []
  for (const r of rows) {
    const s = r[streamCol]
    const n = Number(r[countCol])
    if (typeof s !== 'string' || !s || !Number.isFinite(n)) continue
    out.push({ stream: s, count: n })
  }
  return out.sort((a, b) => b.count - a.count || a.stream.localeCompare(b.stream))
}

/** The engine's wording when a predicate names a column no stream carries. */
export function isUnresolvedColumn(message: string | null | undefined): boolean {
  return /UNRESOLVED_COLUMN/i.test(message ?? '')
}

/* -------------------------------------------------------------- cost gate */

/**
 * Above this many scanned events the search asks before running. Measured
 * 2026-08-26: a 24h `stream=*` scan on the gotham scope priced at 9.3e10
 * `evt_len` regardless of predicate, so the floor sits above one day and
 * below a week — the default window never gates, widening does.
 */
export const COST_GATE_EVENTS = 2e11

export function costGateKey(clusterId: string, scopeId: string): string {
  return `bloo.entity.costgate.${clusterId}.${scopeId}`
}

export function loadCostGate(key: string): number {
  try {
    const v = Number(globalThis.localStorage?.getItem(key))
    return Number.isFinite(v) && v > 0 ? v : COST_GATE_EVENTS
  } catch {
    return COST_GATE_EVENTS
  }
}

/* ---------------------------------------------------------------- recents */

export interface RecentEntity {
  kind: EntityKind
  value: string
  at: number
}

const RECENT_MAX = 20

export function recentsKey(clusterId: string, scopeId: string): string {
  return `bloo.entity.recent.${clusterId}.${scopeId}`
}

export function loadRecents(key: string): RecentEntity[] {
  try {
    const raw = globalThis.localStorage?.getItem(key)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is RecentEntity =>
        typeof e === 'object' && e !== null &&
        isEntityKind((e as RecentEntity).kind) &&
        typeof (e as RecentEntity).value === 'string' &&
        typeof (e as RecentEntity).at === 'number',
    )
  } catch {
    return []
  }
}

export function recordRecent(key: string, entry: Omit<RecentEntity, 'at'> & { at?: number }): void {
  try {
    const at = entry.at ?? Date.now()
    const rest = loadRecents(key).filter((e) => !(e.kind === entry.kind && e.value === entry.value))
    const next = [{ kind: entry.kind, value: entry.value, at }, ...rest].slice(0, RECENT_MAX)
    globalThis.localStorage?.setItem(key, JSON.stringify(next))
  } catch {
    // Convenience only.
  }
}

/* -------------------------------------------------------------------- url */

/** The search params an entity search lives under on /fabric/search. */
export interface EntitySearchParams {
  mode: 'entity'
  type: EntityKind
  value: string
  /** A duration (`24h`) — absent when from/to carry an absolute window. */
  window?: string
  from?: string
  to?: string
  /** Also match the raw LogEvent line — the untranslated-field escape hatch. */
  raw?: boolean
}

export function entityTime(p: Pick<EntitySearchParams, 'window' | 'from' | 'to'>): EntityTime {
  if (p.from && p.to) return { kind: 'absolute', start: p.from, end: p.to }
  return { kind: 'relative', duration: p.window && /^\d+[smhd]$/.test(p.window) ? p.window : DEFAULT_WINDOW }
}

/** Identity of one search — the hook re-runs when this changes. */
export function entitySpecKey(p: EntitySearchParams): string {
  return [p.type, p.value, p.window ?? '', p.from ?? '', p.to ?? '', p.raw ? 'raw' : ''].join(' ')
}
